// src/pages/commerce/CommerceCenter.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  ShoppingBagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { apiGet } from "../../api/client";
import ProBadge from "../../components/ProBadge";
import UpgradeModal from "../../components/UpgradeModal";
import { A } from "../../styles/appleClasses";
import type { AuthMe } from "../../types/auth";
import { companyRoute } from "../../utils/routeHelpers";

function isProUser(me: AuthMe | null) {
  const tier = (me?.account_tier || "").toLowerCase();
  return (
    !!me?.is_user_pro ||
    tier === "pro" ||
    tier === "paid" ||
    tier === "enterprise"
  );
}

export default function CommerceCenter() {
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const [me, setMe] = useState<AuthMe | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    let active = true;

    apiGet<AuthMe>("/auth/me")
      .then((res) => {
        if (active) setMe(res);
      })
      .catch(() => {
        if (active) setMe(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const isPro = isProUser(me);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const tileBase =
    `relative flex flex-col items-center justify-center ` +
    `h-[180px] sm:h-[220px] ` +
    `${A.card} ` +
    `px-4 py-6 sm:px-6 sm:py-8 ` +
    `hover:shadow-md hover:border-gray-300 transition ` +
    `focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  const lockedTile = `${tileBase} opacity-70 cursor-pointer select-none`;
  const tileTitle = `${A.title} text-center leading-tight`;
  const tileDesc = `${A.label} mt-1 text-center`;
  const tileIcon = "w-7 h-7 mb-2 text-appleblue";

  const handleLockedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setShowUpgrade(true);
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="sticky top-0 z-30 border-b border-gray-100 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70">
        <div className="mx-auto mb-2 flex w-full max-w-3xl items-center px-2 pb-2 pt-4 sm:px-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label="Back"
          >
            <ArrowLeftIcon className="h-6 w-6 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <h1 className={`flex-1 text-center ${A.title}`}>Commerce</h1>

          <button
            type="button"
            onClick={() => navigate(companyRoute(company_id, "/dashboard"))}
            className={iconBtn}
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <main className="mx-auto w-full max-w-3xl px-2 pb-8 sm:px-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {isPro ? (
            <Link
              to={companyRoute(company_id, "/commerce/shopify")}
              className={tileBase}
            >
              <ShoppingBagIcon className={tileIcon} />
              <span className={tileTitle}>Shopify</span>
              <span className={tileDesc}>Orders, sync issues, and activity</span>
            </Link>
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowUpgrade(true)}
              onKeyDown={handleLockedKeyDown}
              className={lockedTile}
            >
              <ProBadge overlay size="xs" />
              <ShoppingBagIcon className={tileIcon} />
              <span className={tileTitle}>Shopify Commerce</span>
              <span className={tileDesc}>Orders, sync issues, and activity</span>
              <span className="mt-2 text-center text-[11px] font-medium text-gray-500">
                Upgrade to unlock Shopify Commerce
              </span>
            </div>
          )}
        </div>
      </main>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
    </div>
  );
}